import {
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { BhiDialog, MediaCardPreviewFrame } from 'src/UIKit';
import { FilterSelect } from './FilterSelect.styles';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  TextField,
  Typography,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';

import { Breadcrumb } from 'src/components/Breadcrumb';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { TableHeader, TableHeaderItem, TABLE_HEADER_ACTION_SX } from 'src/components/TableHeader/TableHeader';
import PrivateLayoutView from 'src/Layout/PrivateLayout/PrivateLayoutView';
import LoadingButton from '@mui/lab/LoadingButton';
import SaveIcon from '@mui/icons-material/Save';
import { useAssetLibraryViewController } from '../../controllers';
import { MediaInfo } from '../../types';
import { AssetCard, AssetCardAsset } from '../AssetCard/AssetCard';
import {
  FileContainer,
  ErrorMessage,
  Cont,
} from 'src/components/AttachFileUrlModal/styles';
import { useDropzoneStyles } from 'src/components/AttachFileUrlModal/useDropzoneStyles';
import VcfDisplayFromUrl from 'src/components/VcfViewer/view';
import { CsvPreview } from 'src/components/CsvPreview';
import { CanvaDesignsPreview } from 'src/modules/canva/components/CanvaDesignsPreview';
import { FileLinkWithMimeType } from 'src/constants/types';
import { useCanvaService } from 'src/modules/canva/services';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useSelector } from 'react-redux';
import { isDocumentType } from 'src/modules/assetLibrary/helpers'
import { logSafeAxiosError, reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { GENERIC_ERROR_MESSAGE, FILE_UPLOAD_ERROR_MESSAGE } from 'src/constants/messages';
import { getFileDisplayNameAndExtension, splitFileNameAndExtension } from 'src/helpers/files';

const MIME_TYPE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'image/jpeg,image/png,image/gif,image/bmp,image/tiff,image/webp,image/heif,image/heic,image/avif,image/svg+xml,image/x-icon,image/vnd.microsoft.icon,image/apng,image/x-pcx,image/x-portable-bitmap,image/x-portable-pixmap,image/x-portable-graymap,image/x-targa,image/x-tga,image/x-xbitmap,image/x-xpixmap,image/ktx,image/vnd.wap.wbmp', label: 'Images' },
  { value: 'video/mp4,video/webm,video/ogg,video/x-msvideo,video/x-ms-wmv,video/mpeg,video/3gpp,video/3gpp2,video/quicktime,video/x-flv,video/x-matroska,video/h261,video/h263,video/h264,video/h265,video/mp2t,video/vnd.dlna.mpeg-tts,video/x-f4v', label: 'Videos' },
  { value: 'audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/webm,audio/ogg,audio/opus,audio/aac,audio/mp4,audio/3gpp,audio/3gpp2,audio/x-ms-wma,audio/flac,audio/x-flac,audio/amr,audio/midi,audio/x-midi,audio/x-aiff,audio/basic', label: 'Audio Files' },
  { value: 'text/vcard,text/x-vcard,text/directory,application/vcard+json,application/vcard+xml', label: 'Contact Cards' },
  { value: 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,text/rtf,text/html,application/json,application/xml,application/epub+zip,application/vnd.oasis.opendocument.text,application/vnd.o', label: 'Documents' },
  { value: 'text/calendar,application/calendar+xml,application/icalendar,application/vnd.apple.keynote,application/vnd.google-apps.calendar', label: 'Calendar' },
];

const SCROLL_THROTTLE_MS = 150;

export const AssetLibraryView = () => {
  const {
    isPageLoading,
    isLoadingMore,
    isUploading,
    isUpdating,
    assetLibraryFilter,
    assetList,
    changeAssetSearchFilter,
    changeMimeTypeFilter,
    reloadAssets,
    removeAsset,
    uploadAsset,
    updateAssetName,
    updateAssetList,
    loadMore,
  } = useAssetLibraryViewController();
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollThrottleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { openDialog, ConfirmationDialog } = useConfirmationDialog();
  const { getDesign } = useCanvaService();

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTab, setUploadTab] = useState<'file' | 'canva'>('file');
  const [canvaPreview, setCanvaPreview] = useState<{
    file: File;
    url: string;
    designName: string;
  } | null>(null);
  const [isCanvaExporting, setIsCanvaExporting] = useState(false);
  const [selectedCanvaDesign, setSelectedCanvaDesign] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingOriginalExtension, setEditingOriginalExtension] = useState('');

  const [previewAsset, setPreviewAsset] = useState<MediaInfo | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const reconstructFileName = (name: string, extension?: string) => {
    return extension ? `${name}.${extension}` : name;
  };

  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      setUploadFile(file);
      if (!uploadFileName.trim()) {
        const parsed = splitFileNameAndExtension(file.name);
        setUploadFileName(parsed.name || '');
      }
    }
  };

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    onDrop: handleFileSelect,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
      'video/*': ['.mp4', '.mov', '.avi'],
      'audio/*': ['.mp3', '.wav', '.m4a'],
      'application/pdf': ['.pdf'],
      'text/*': ['.txt', '.csv'],
      'application/*': ['.doc', '.docx', '.xls', '.xlsx'],
      'text/vcard': ['.vcf', '.vcard'],
    },
    maxFiles: 1,
  });

  const style = useDropzoneStyles({ isFocused, isDragAccept, isDragReject });

  const resetUploadState = useCallback(() => {
    setUploadModalOpen(false);
    setUploadFile(null);
    setUploadFileName('');
    setCanvaPreview(null);
    setUploadTab('file');
    setIsCanvaExporting(false);
  }, []);

  const refreshAssetLibrary = useCallback(async () => {
    try {
      await reloadAssets();
    } catch (error) {
      logSafeAxiosError(error);
    }
  }, [reloadAssets]);

  const closeModalAndRefresh = useCallback(async () => {
    resetUploadState();
    await refreshAssetLibrary();
  }, [resetUploadState, refreshAssetLibrary]);

  const handleUpload = async () => {
    if (!uploadFileName.trim()) return;

    try {
      if (selectedCanvaDesign && uploadTab === 'canva') {
        setIsCanvaExporting(true);
        const design = await getDesign(
          selectedCompanyId!,
          selectedCanvaDesign.id,
          uploadFileName.trim(),
        );
        if (design) {
          setSelectedCanvaDesign(null);
        } else {
          throw new Error('Failed to get Canva design');
        }

        setIsCanvaExporting(false);
      } else if (canvaPreview) {
        const originalParsed = splitFileNameAndExtension(canvaPreview.file.name);
        const uploadedAsset = await uploadAsset(canvaPreview.file, canvaPreview.file.name);

        const customName = uploadFileName.trim();
        const originalDesignName = canvaPreview.designName;
        if (customName !== originalDesignName && uploadedAsset) {
          const fullCustomName = reconstructFileName(customName, originalParsed.extension);
          await updateAssetName(uploadedAsset.id, fullCustomName);
        }
      } else if (uploadFile) {
        const originalParsed = splitFileNameAndExtension(uploadFile.name);
        const fullUploadName = reconstructFileName(uploadFileName.trim(), originalParsed.extension);
        await uploadAsset(uploadFile, fullUploadName);
      }
      resetUploadState();
      await refreshAssetLibrary();
    } catch (error) {
      reportErrorToUser(error, FILE_UPLOAD_ERROR_MESSAGE);
      setIsCanvaExporting(false);
    }
  };

  const handleCancelUpload = useCallback(() => {
    resetUploadState();
  }, [resetUploadState]);

  const handleCanvaExportEnd = useCallback(async () => {
    setIsCanvaExporting(false);
    await closeModalAndRefresh();
  }, [closeModalAndRefresh]);

  const handleCanvaAttach = async (link: FileLinkWithMimeType, designName?: string) => {
    try {
      const response = await fetch(link.url);
      const blob = await response.blob();
      const actualDesignName = designName || 'Canva Design';
      const fileName = designName || 'Canva Design';
      const file = new File([blob], fileName, { type: link.mimeType });

      const previewUrl = URL.createObjectURL(blob);
      setCanvaPreview({ file, url: previewUrl, designName: actualDesignName });
      setUploadFileName(actualDesignName);
      setIsCanvaExporting(false);
    } catch (error) {
      reportErrorToUser(error, GENERIC_ERROR_MESSAGE);
      setIsCanvaExporting(false);
    }
  };

  const handleCanvaDesignSelect = (designId: string, designTitle: string) => {
    setSelectedCanvaDesign({ id: designId, title: designTitle });
    setUploadFileName(designTitle);
  };

  const handleEditStart = useCallback((asset: AssetCardAsset) => {
    const parsed = getFileDisplayNameAndExtension(asset.name, asset.mimeType);
    setEditingAssetId(asset.id);
    setEditingName(parsed.name);
    setEditingOriginalExtension(parsed.extension ?? '');
  }, []);

  const handleEditSave = useCallback(
    async (assetId: string) => {
      if (editingName.trim() === '') return;

      try {
        const fullName = reconstructFileName(editingName.trim(), editingOriginalExtension);
        await updateAssetName(assetId, fullName);

        // Update local state to prevent full refresh
        if (assetList?.items) {
          const updatedItems = assetList.items.map(asset =>
            asset.id === assetId ? { ...asset, name: fullName } : asset,
          );
          updateAssetList({ items: updatedItems, count: assetList.count });
        }

        setEditingAssetId(null);
        setEditingName('');
        setEditingOriginalExtension('');
      } catch (error) {
        logSafeAxiosError(error);
      }
    },
    [
      editingName,
      editingOriginalExtension,
      updateAssetName,
      reconstructFileName,
      assetList,
      updateAssetList,
    ],
  );

  const handleEditCancel = useCallback(() => {
    setEditingAssetId(null);
    setEditingName('');
    setEditingOriginalExtension('');
  }, []);

  const handleDelete = useCallback((asset: AssetCardAsset) => {
    openDialog({
      title: `Delete "${asset.name || 'Untitled'}"`,
      content: 'Are you sure you want to delete this asset? This action cannot be undone.',
      onConfirm: () => removeAsset(asset.id),
    });
  }, [openDialog, removeAsset]);

  const handleImageError = useCallback((assetId: string) => {
    setFailedImages(prev => new Set([...prev, assetId]));
  }, []);

  const runScrollCheck = useCallback(() => {
    if (!scrollContainerRef.current || isLoadingMore || isPageLoading) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const scrollThreshold = 200;

    if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
      if (assetList && assetList.items.length < assetList.count) {
        loadMore();
      }
    }
  }, [isLoadingMore, isPageLoading, assetList, loadMore]);

  const throttledHandleScroll = useCallback(() => {
    if (scrollThrottleTimeoutRef.current) return;

    scrollThrottleTimeoutRef.current = setTimeout(() => {
      scrollThrottleTimeoutRef.current = null;
      runScrollCheck();
    }, SCROLL_THROTTLE_MS);
  }, [runScrollCheck]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    scrollContainer.addEventListener('scroll', throttledHandleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', throttledHandleScroll);
      if (scrollThrottleTimeoutRef.current) {
        clearTimeout(scrollThrottleTimeoutRef.current);
        scrollThrottleTimeoutRef.current = null;
      }
    };
  }, [throttledHandleScroll]);

  return (
    <>
      <ConfirmationDialog />

      <>
        <PrivateLayoutView
          title={
            <Breadcrumb items={[{ label: 'Asset Library', isActive: true }]} />
          }
        >
          <Box mb={2} borderRadius={1.5} border={1} borderColor="grey.200" overflow="hidden">
            <Box p={2} borderBottom={1} borderColor="grey.200">
              <TableHeader
                searchValue={assetLibraryFilter.query || ''}
                onSearchChange={(_, value) => changeAssetSearchFilter(value)}
                searchPlaceholder="Search assets..."
                filters={
                  <TableHeaderItem>
                    <FormControl fullWidth>
                      <FilterSelect
                        labelId="filter-by-type-label"
                        value={assetLibraryFilter.mime_type || ''}
                        onChange={e => changeMimeTypeFilter(e.target.value as string)}
                        displayEmpty
                        active={!!assetLibraryFilter.mime_type}
                      >
                        {MIME_TYPE_FILTERS.map(filter => (
                          <MenuItem key={filter.value} value={filter.value}>
                            <Typography variant="body2">{filter.label}</Typography>
                          </MenuItem>
                        ))}
                      </FilterSelect>
                    </FormControl>
                  </TableHeaderItem>
                }
                actions={
                  <Button
                    onClick={() => setUploadModalOpen(true)}
                    variant="contained"
                    sx={TABLE_HEADER_ACTION_SX}
                  >
                    Upload Asset
                  </Button>
                }
              />
            </Box>

            <Box
              ref={scrollContainerRef}
              height="calc(100vh - 320px)"
              overflow="auto"
              p={2}
            >

              {isPageLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                  <CircularProgress />
                </Box>
              ) : !assetList?.items?.length ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                  <Typography variant="body1" color="text.secondary">
                    {assetLibraryFilter.query || assetLibraryFilter.mime_type
                      ? `No assets found matching your criteria.${assetLibraryFilter.mime_type ? ` Try changing the filter or search terms.` : ''}`
                      : 'No assets uploaded yet. Click Upload to add your first asset.'}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Grid container spacing={3} paddingTop={1} paddingBottom={1} paddingLeft={1}>
                    {assetList.items.map((asset) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={`${asset.id}`}>
                        <AssetCard
                          asset={asset}
                          isEditing={editingAssetId === asset.id}
                          editingName={editingName}
                          failedImages={failedImages}
                          isUpdating={isUpdating}
                          onEditStart={handleEditStart}
                          onEditSave={handleEditSave}
                          onEditCancel={handleEditCancel}
                          onEditNameChange={setEditingName}
                          onDelete={handleDelete}
                          onImageError={handleImageError}
                          onPreview={() => setPreviewAsset(asset)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {isLoadingMore && (
                    <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                      <CircularProgress size={32} />
                    </Box>
                  )}
                  {assetList.items.length >= assetList.count && assetList.count > 30 && (
                    <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                      <Typography variant="body2" color="text.secondary">
                        All assets loaded ({assetList.count} total)
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>

          <BhiDialog
            open={uploadModalOpen}
            onClose={handleCancelUpload}
            maxWidth="sm"
            fullWidth
            title="Upload Asset"
          >
            <DialogContent>
              <Box sx={{ mb: 1 }}>
                <TextField
                  fullWidth
                  value={uploadFileName}
                  onChange={e => setUploadFileName(e.target.value)}
                  autoFocus
                  disabled={isUploading || isCanvaExporting}
                  placeholder="Enter asset name"
                />
              </Box>

              <Box borderBottom={1} borderColor="divider" mb={2}>
                <Tabs value={uploadTab} onChange={(_, newValue) => setUploadTab(newValue)}>
                  <Tab label="File Upload" value="file" disabled={isCanvaExporting} />
                  <Tab label="Import from Canva" value="canva" disabled={isCanvaExporting} />
                </Tabs>
              </Box>

              {uploadTab === 'file' && (
                <FileContainer style={{ marginBottom: '10px' }}>
                  <div className="container">
                    <Cont {...getRootProps({ style })}>
                      <input {...getInputProps()} />
                      <p>Drag and drop file here, or click to select file</p>
                      <div style={{ textAlign: 'center', padding: '0px 40px' }}>
                        Allowed formats: Images, Videos, Audio, PDF, Documents, VCards
                      </div>
                      {isUploading ? (
                        <LoadingButton
                          loading
                          loadingPosition="start"
                          startIcon={<SaveIcon />}
                          variant="outlined"
                          style={{ margin: '25px 0px 10px 0px' }}
                        >
                          Loading
                        </LoadingButton>
                      ) : (
                        <Button
                          component="label"
                          role={undefined}
                          variant="contained"
                          tabIndex={-1}
                          disabled={isUploading}
                          startIcon={<CloudUploadIcon />}
                          style={{ margin: '25px 0px 10px 0px' }}
                        >
                          {uploadFile ? uploadFile.name : 'Upload'}
                        </Button>
                      )}
                    </Cont>
                  </div>
                  <ErrorMessage></ErrorMessage>
                </FileContainer>
              )}

              {uploadTab === 'canva' && (
                <>
                  {canvaPreview ? (
                    <Box display="flex" flexDirection="column" alignItems="center" height="100%" justifyContent="center">
                      <Box
                        component="img"
                        src={canvaPreview.url}
                        alt="Canva Design Preview"
                        borderRadius={1}
                        border={1}
                        borderColor="divider"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '300px',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  ) : (
                    <CanvaDesignsPreview
                      attachImage={handleCanvaAttach}
                      handleClose={handleCancelUpload}
                      hideButtons={true}
                      onExportStart={() => setIsCanvaExporting(true)}
                      onExportEnd={handleCanvaExportEnd}
                      onDesignSelect={handleCanvaDesignSelect}
                      selectedDesignId={selectedCanvaDesign?.id || null}
                      selectionMode={true}
                    />
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions
              sx={{
                marginBottom: 2,
              }}
            >
              <Button variant="outlined" onClick={handleCancelUpload} disabled={isUploading}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                variant="contained"
                disabled={
                  !uploadFileName.trim() ||
                  isUploading ||
                  isCanvaExporting ||
                  (uploadTab === 'canva' && !selectedCanvaDesign)
                }
              >
                {isUploading ? <CircularProgress size={20} /> : 'Import'}
              </Button>
            </DialogActions>
          </BhiDialog>
        </PrivateLayoutView>
      </>

      {/* Asset Preview Modal */}
      <BhiDialog
        open={!!previewAsset}
        onClose={() => setPreviewAsset(null)}
        maxWidth="md"
        fullWidth
        title={previewAsset?.name || 'Asset Preview'}
      >
        <DialogContent>
          {previewAsset && (
            <Box sx={{ textAlign: 'center', p: 2, paddingTop: 0 }}>
              {previewAsset.mimeType.startsWith('image/') ? (
                <img
                  src={previewAsset.url}
                  alt={previewAsset.name || 'Asset'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                  }}
                />
              ) : previewAsset.mimeType === 'text/vcard' ||
                previewAsset.mimeType === 'text/x-vcard' ? (
                <Box sx={{ p: 2 }}>
                  <VcfDisplayFromUrl vcfUrl={previewAsset.url} />
                </Box>
              ) : previewAsset.mimeType.startsWith('video/') ? (
                <video
                  controls
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                  }}
                >
                  <source src={previewAsset.url} type={previewAsset.mimeType} />
                </video>
              ) : previewAsset.mimeType.startsWith('audio/') ? (
                <Box>
                  <audio controls style={{ width: '100%', marginBottom: '16px' }}>
                    <source src={previewAsset.url} type={previewAsset.mimeType} />
                  </audio>
                  <Typography variant="body2">Audio file: {previewAsset.name}</Typography>
                </Box>
              ) : isDocumentType(previewAsset.mimeType) ? (
                <MediaCardPreviewFrame height="70vh">
                  {previewAsset.mimeType === 'application/pdf' ? (
                    <iframe
                      src={`${previewAsset.url}#toolbar=0&navpanes=0&scrollbar=0`}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                      }}
                      title={`PDF Preview: ${previewAsset.name}`}
                    />
                  ) : previewAsset.mimeType === 'text/csv' ? (
                    <CsvPreview 
                      csvUrl={previewAsset.url} 
                      maxRows={1000} 
                      width="100%" 
                      height="100%" 
                    />
                  ) : previewAsset.mimeType.startsWith('text/') || 
                         previewAsset.mimeType === 'application/json' || 
                         previewAsset.mimeType === 'application/xml' ? (
                    <iframe
                      src={previewAsset.url}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        backgroundColor: 'white',
                      }}
                      title={`Text Document Preview: ${previewAsset.name}`}
                    />
                  ) : (
                    <iframe
                      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewAsset.url)}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                      }}
                      title={`Document Preview: ${previewAsset.name}`}
                      onError={(e) => {
                        console.warn('Office Apps preview failed, trying Google Docs viewer');
                        const iframe = e.target as HTMLIFrameElement;
                        iframe.src = `https://docs.google.com/gview?url=${encodeURIComponent(previewAsset.url)}&embedded=true`;
                      }}
                    />
                  )}
                </MediaCardPreviewFrame>
              ) : (
                <Box sx={{ p: 4 }}>
                  <Typography component="div" color="text.secondary" fontSize={64} lineHeight={1} mb={2}>
                    <EventIcon fontSize="inherit" />
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {previewAsset.name || 'Calendar Event'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    No preview available for calendar events.
                  </Typography>
                  <Button
                    variant="contained"
                    href={previewAsset.url}
                    download={previewAsset.name}
                    sx={{ mt: 2 }}
                  >
                    Download Calendar File
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setPreviewAsset(null)}>
            Close
          </Button>
        </DialogActions>
      </BhiDialog>
    </>
  );
};
